import React, { useState, useEffect } from 'react';
import { SortableList } from '../../../components/SortableList/SortableList';
import { Card } from '../../../components/Card/Card';
import classnames from 'classnames';
import {
  Product,
  MerchProductVariant,
  updateVariants,
  getProduct,
  deleteVariant,
  getProductSaleStats,
} from '../../../model/product';
import { sortBy } from 'lodash';
import { ProductVariantsForm } from './Modals/ProductVariantsForm';
import { Form } from '../../../components/Form/Form';
import { Select } from '../../../components/Form/Select/Select';
import { handleError } from '../../../model/notification';
import { StatusPill } from '../../../components/StatusPill/StatusPill';
import { formatDateTime, present } from '../../../util/util';
import { ControlledModal } from '../../../components/Modal/ControlledModal';
import { PermissionButton } from '../../../components/Button/PermissionButton';
import { useConfirmation } from '../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { Stack } from '../../../components/Stack/Stack';
import { t } from '../../../locales/translate';
import { isAdmin, useSelectedMembership } from '../../../model/membership';
import { warehousePath } from '../../../routes';

interface Props {
  product: Product;
  onSort?: () => void;
}

export function getVariantActiveState(variant: MerchProductVariant): JSX.Element {
  if (variant.active) {
    return <StatusPill status="active" />;
  } else {
    if (variant.activeFrom) {
      const today = new Date();
      const future = variant.activeFrom > today;
      if (future) {
        return <span>Active on {formatDateTime(variant.activeFrom)}</span>;
      }
    }
  }
  return <StatusPill status="inactive" />;
}

export function ProductVariants({ product }: Props): JSX.Element {
  const memberhip = useSelectedMembership();
  const admin = isAdmin(memberhip);
  const [filter, setFilter] = useState({ status: '' });
  // Edit / Add
  const [displayModal, setDisplayModal] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<MerchProductVariant | undefined>(undefined);
  // Delete
  const [variantForDelete, setVariantForDelete] = useState<MerchProductVariant | undefined>(undefined);
  // Sorting
  const [loadingSort, setLoadingSort] = useState(false);

  function hasProductVariants(): boolean {
    if (product.productVariants && product.productVariants.length > 0) {
      return true;
    }
    return false;
  }

  function getOrderIndex(variantId: string): [number, MerchProductVariant[]] {
    const variants = [...sortBy(product.productVariants, ['order'])];
    return [variants.findIndex((item) => item.id === variantId), variants];
  }

  async function sortProduct(variant: MerchProductVariant, direction: 'up' | 'down'): Promise<void> {
    const [variantIndex, variants] = getOrderIndex(variant.id);
    let subVariants: MerchProductVariant[] = [];

    setLoadingSort(true);
    try {
      if (direction === 'up') {
        subVariants = variants.slice(0, variantIndex + 1);
        subVariants[variantIndex] = variants[variantIndex - 1];
        subVariants[variantIndex - 1] = variants[variantIndex];
        await updateVariants([
          ...subVariants.map((item, index) => {
            item.order = index;
            return item;
          }),
        ]);
      }
      if (direction === 'down') {
        subVariants = variants.slice(variantIndex);
        subVariants[0] = variants[variantIndex + 1];
        subVariants[1] = variants[variantIndex];
        await updateVariants([
          ...subVariants.map((item, index) => {
            item.order = index + variantIndex;
            return item;
          }),
        ]);
      }
      await getProduct(product.id);
    } catch (error) {
      handleError(error);
    }
    setLoadingSort(false);
  }

  async function handleDeleteVariant(): Promise<void> {
    if (variantForDelete) {
      await deleteVariant(variantForDelete.id, product.id);
      getProductSaleStats(product.id);
      setVariantForDelete(undefined);
    }
  }

  const { confirm } = useConfirmation({
    title: t.actions.delete(t.model.productVariant._name),
    message: t.general.confirmDelete(t.model.productVariant._name),
    action: handleDeleteVariant,
    onClose: () => setVariantForDelete(undefined),
  });

  useEffect(() => {
    if (variantForDelete) {
      confirm();
    }
  }, [variantForDelete]);

  return (
    <>
      <Card
        title={`${t.model.product._name} ${t.model.variant._plural}`}
        actions={
          <PermissionButton onClick={() => setDisplayModal(true)} name="plus" model={product} permission="isAdmin" />
        }
        fullbleed={hasProductVariants()}
      >
        {!hasProductVariants() && (
          <span>{t.errors.noItemsforThisCollection(t.model.productVariant._plural, t.model.product._name)}</span>
        )}
        {hasProductVariants() && product.productVariants && (
          <SortableList
            loading={loadingSort}
            items={product.productVariants
              .map((v) => ({
                ...v,
                edit: 'edit',
              }))
              .filter((v) => {
                if (filter.status === 'active') {
                  return v.active;
                }
                if (filter.status === 'inactive') {
                  return !v.active;
                }
                return v;
              })}
            actions={
              <Form className="filter-bar" model={filter} onInput={(value) => setFilter(value)}>
                <Select
                  name="status"
                  label={t.model.status._name}
                  options={[
                    { value: 'active', label: t.model.status.active },
                    {
                      value: 'inactive',
                      label: t.model.status.inactive,
                    },
                  ]}
                  allowEmpty
                />
              </Form>
            }
            defaultSort={{ sortBy: 'order', sortAscending: true }}
            columns={[
              {
                dataKey: 'sku',
                label: 'SKU',
                cellRenderer: (row) => row.sku || '-',
              },
              {
                label: 'VARIATION',
                dataKey: 'description',
                cellRenderer: (row) => row.description || '-',
              },
              {
                label: 'Weight',
                dataKey: 'weight',
                cellRenderer: (row) => row.weight || '-',
              },
              {
                label: 'Min. Stock',
                dataKey: 'minimumStockCount',
                cellRenderer: (row) => row.minimumStockCount || '-',
              },
              {
                label: t.model.productVariant.ean,
                dataKey: 'ean',
              },
              product.productVariants.some((variant) => present(variant.legacyId)) && {
                label: t.general.legacyId,
                dataKey: 'legacyId',
                cellRenderer: (row) => row.legacyId ?? '-',
              },
              {
                label: 'Order',
                dataKey: 'order',
                cellRenderer: (row) => (row.order !== null ? row.order : '-'),
              },
              {
                label: t.model.warehouseLocation._plural,
                dataKey: 'warehouseLocations',
                cellRenderer: (row) => {
                  if (!row.warehouseLocations || row.warehouseLocations.length === 0) {
                    return '-';
                  }
                  return (
                    <Stack size="zero">
                      {row.warehouseLocations.map((location, index) => (
                        <a href={warehousePath() + `?locationId=${location.id}`} key={index}>
                          {location.name} {location.tags && location.tags.length > 0 && `(${location.tags.join(', ')})`}
                        </a>
                      ))}
                    </Stack>
                  );
                },
              },
              {
                label: 'Status',
                dataKey: 'active',
                cellRenderer: (row) => (
                  <>
                    {getVariantActiveState(row)}
                    {row.private && (
                      <div>
                        <StatusPill status="private" />
                      </div>
                    )}
                  </>
                ),
              },
              {
                sortable: false,
                dataKey: 'edit',
                cellRenderer: (row) => (
                  <div className="product-variants__actions">
                    <PermissionButton
                      className={classnames({
                        'icon--disabled':
                          product.productVariants && getOrderIndex(row.id)[0] === product.productVariants.length - 1,
                      })}
                      model={row}
                      permission="update"
                      name="arrow-line-down--sm"
                      onClick={() => {
                        sortProduct(row, 'down');
                      }}
                      hideLock={!admin}
                    />
                    <PermissionButton
                      className={classnames({
                        'icon--disabled': getOrderIndex(row.id)[0] === 0,
                      })}
                      model={row}
                      permission="update"
                      name="arrow-line-up--sm"
                      onClick={() => {
                        sortProduct(row, 'up');
                      }}
                      hideLock={!admin}
                    />
                    <PermissionButton
                      model={row}
                      permission="update"
                      name="edit"
                      className="product-variants__edit"
                      onClick={() => {
                        setDisplayModal(true);
                        setSelectedVariant(row);
                      }}
                      hideLock={!admin}
                    />
                    <PermissionButton
                      model={row}
                      permission="destroy"
                      name="trash"
                      className="product-variants__edit"
                      onClick={() => setVariantForDelete(row)}
                      hideLock={!admin}
                    />
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>
      <ControlledModal<Partial<MerchProductVariant>>
        open={displayModal}
        model={{ ...selectedVariant }}
        onClose={() => {
          setSelectedVariant(undefined);
          setDisplayModal(false);
        }}
        title={t.actions[selectedVariant ? 'edit' : 'add'](t.model.variant._name)}
        size="small"
      >
        {(controller) => <ProductVariantsForm controller={controller} product={product} />}
      </ControlledModal>
    </>
  );
}
